import { useRef, useState, useCallback } from 'react'
import {
	Plus,
	PencilSimple,
	ArrowRight,
	CheckCircle,
	XCircle,
	Archive,
	ArrowsClockwise,
	ChatCircle,
	GitBranch,
	UserCircle,
	ArrowUUpLeft,
} from '@phosphor-icons/react'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@journ/ui/accordion'
import { Badge } from '@journ/ui/badge'
import { Button } from '@journ/ui/button'
import { Separator } from '@journ/ui/separator'
import { Switch } from '@journ/ui/switch'
import { Textarea } from '@journ/ui/textarea'
import { UserAvatar } from '@/components/user-avatar'
import { useIntlayer } from 'react-intlayer'
import { useSelector } from '@/libs/state'
import { useDateFormat } from '@/features/preferences/hooks/use-date-format'
import type { WorkItemHistoryEntry } from '../../domain/workflow'
import { commentBody$ } from '../../work-item-detail-store'
import { historyDetail, historyLabel, modeLabel } from '../work-item-detail-labels'
import { useWorkItemDetailContext } from './work-item-detail-context'

const historyIcon = (action: WorkItemHistoryEntry['action']) => {
	const cls = 'size-3.5 shrink-0'
	if (action === 'work_item.created') return <Plus className={`${cls} text-foreground/60`} />
	if (action === 'work_item.completed') return <CheckCircle className={`${cls} text-green-500`} />
	if (action === 'work_item.cancelled' || action === 'phase.rejected') return <XCircle className={`${cls} text-red-400`} />
	if (action === 'work_item.archived') return <Archive className={`${cls} text-606060`} />
	if (action === 'work_item.reopened') return <ArrowUUpLeft className={`${cls} text-blue-500`} />
	if (action === 'work_item.work_state_changed') return <ArrowsClockwise className={`${cls} text-blue-500`} />
	if (action === 'step.transitioned' || action === 'phase.handed_off' || action === 'phase.accepted')
		return <ArrowRight className={`${cls} text-blue-500`} />
	if (action === 'comment.added' || action === 'comment.edited') return <ChatCircle className={`${cls} text-violet-500`} />
	if (action.startsWith('spawn.')) return <GitBranch className={`${cls} text-amber-500`} />
	if (action === 'work_item.assigned' || action.includes('reassign')) return <UserCircle className={`${cls} text-606060`} />
	return <PencilSimple className={`${cls} text-606060`} />
}

type InlineTextareaProps = {
	value: string
	onCommit: (value: string) => void
	className?: string
	rows?: number
	placeholder?: string
}

const InlineTextarea = ({ value: externalValue, onCommit, className, rows = 1, placeholder }: InlineTextareaProps) => {
	const [draft, setDraft] = useState<string | null>(null)
	const textareaRef = useRef<HTMLTextAreaElement>(null)

	const syncHeight = useCallback(() => {
		const el = textareaRef.current
		if (!el) return
		el.style.height = 'auto'
		el.style.height = `${el.scrollHeight}px`
	}, [])

	const commit = () => {
		if (draft !== null) {
			onCommit(draft)
			setDraft(null)
		}
	}

	return (
		<textarea
			ref={textareaRef}
			value={draft ?? externalValue}
			rows={rows}
			placeholder={placeholder}
			onChange={e => {
				setDraft(e.target.value)
				syncHeight()
			}}
			onFocus={syncHeight}
			onBlur={commit}
			onKeyDown={e => {
				if (e.key === 'Escape') {
					setDraft(null)
					e.currentTarget.blur()
				}
			}}
			className={className}
		/>
	)
}

const TitleField = () => {
	const { detail } = useWorkItemDetailContext()
	const { item, setTitle } = detail

	if (!item) return null

	return (
		<InlineTextarea
			value={item.title}
			onCommit={v => {
				const next = v.trim()
				if (next) setTitle(next)
			}}
			className='w-full resize-none overflow-hidden bg-transparent text-2xl font-normal leading-tight outline-none placeholder:text-muted-foreground'
		/>
	)
}

const SubjectField = () => {
	const { detail } = useWorkItemDetailContext()
	const { item, setSubject } = detail
	const c = useIntlayer('work-item').detail

	if (!item) return null

	return (
		<div className='flex flex-col gap-1'>
			<div className='text-[12px] text-606060'>{c.fieldSubject}</div>
			<InlineTextarea
				value={item.subjectName}
				onCommit={v => {
					const next = v.trim()
					if (next) setSubject(next)
				}}
				className='w-full resize-none overflow-hidden bg-transparent text-[14px] leading-relaxed outline-none placeholder:text-muted-foreground/50'
			/>
		</div>
	)
}

const NotesField = () => {
	const { detail } = useWorkItemDetailContext()
	const { item, setNotes } = detail
	const c = useIntlayer('work-item').detail

	if (!item) return null

	return (
		<div className='flex flex-col gap-1'>
			<div className='text-[12px] text-606060'>{c.fieldNotes}</div>
			<InlineTextarea
				value={item.notes ?? ''}
				onCommit={setNotes}
				rows={3}
				placeholder={(c as Record<string, { value: string } | undefined>)['notesPlaceholder']?.value ?? 'Add notes…'}
				className='w-full resize-none overflow-hidden bg-transparent text-[14px] leading-relaxed outline-none placeholder:text-muted-foreground/50 focus:placeholder:opacity-100'
			/>
		</div>
	)
}

const WorkItemMain = () => {
	const { workflow, detail } = useWorkItemDetailContext()
	const { item, spawns, actorCan, memberName, memberAvatarById, conclude, setLightweight, onComment } = detail
	const c = useIntlayer('work-item').detail
	const { formatPreferenceDateTime } = useDateFormat()
	const commentBody = useSelector(commentBody$)

	if (!item) return null

	const currentPhase = workflow.phases.find(p => p.id === item.currentPhaseId)
	const currentStep = currentPhase?.teamWorkflow.steps.find(s => s.id === item.currentStepId)

	return (
		<div className='flex-1 min-w-0 flex flex-col gap-6'>
			<div className='flex flex-col gap-2'>
				<TitleField />
				{(currentPhase || currentStep) ? (
					<div className='flex items-center gap-1.5 text-[12px] text-606060'>
						{currentPhase ? <span>{currentPhase.name}</span> : null}
						{currentPhase && currentStep ? <span>/</span> : null}
						{currentStep ? <span className='text-foreground/80'>{currentStep.name}</span> : null}
					</div>
				) : null}
			</div>

			<SubjectField />

			<NotesField />

			<Separator />
			{spawns.length ? (
				<>
					<section className='flex flex-col gap-3'>
						<h2 className='text-[16px] font-medium'>{c.sectionSpawns}</h2>
						{spawns.length === 0 ? (
							<p className='text-[14px] text-606060'>{c.noSpawns}</p>
						) : (
							<div className='flex flex-col gap-2'>
								{spawns.map(st => {
									const isOpen = st.status !== 'completed' && st.status !== 'cancelled' && st.status !== 'archived'
									return (
										<div key={st.id} className='flex items-center justify-between gap-2 py-1.5'>
											<span className={`text-[14px] ${st.status === 'completed' ? 'line-through text-606060' : ''}`}>
												{st.title}
											</span>
											<div className='flex items-center gap-2 shrink-0'>
												{!st.lightweight ? <Badge variant='tag'>{modeLabel(c, st.spawnMode)}</Badge> : null}
												{isOpen ? (
													<>
														<label className='flex items-center gap-1.5 text-[12px] text-606060'>
															{c.lightweightBadge}
															<Switch
																checked={st.lightweight}
																onCheckedChange={checked => setLightweight(st, checked)}
															/>
														</label>
														{st.lightweight ? (
															<Button size='sm' variant='outline' onClick={() => conclude(st, 'completed')}>
																{c.concludeSpawnAction}
															</Button>
														) : null}
													</>
												) : st.lightweight ? (
													<Badge variant='tag'>{c.lightweightBadge}</Badge>
												) : null}
											</div>
										</div>
									)
								})}
							</div>
						)}
					</section>
					<Separator />
				</>
			) : null}

			<Accordion type='multiple' defaultValue={['comments']}>
				<AccordionItem value='comments' className='border-b-0'>
					<AccordionTrigger className='text-[16px] font-medium py-0'>{c.sectionComments}</AccordionTrigger>
					<AccordionContent className='pt-3'>
						<div className='flex flex-col gap-4'>
							{item.comments.length === 0 ? (
								<p className='text-[14px] text-606060'>{c.noComments}</p>
							) : (
								<div className='flex flex-col gap-4'>
									{item.comments.map(commentEntry => (
										<div key={commentEntry.id} className='flex gap-2'>
											<UserAvatar
												name={commentEntry.authorName}
												avatarUrl={memberAvatarById.get(commentEntry.authorId) ?? null}
												size='sm'
												className='mt-0.5'
											/>
											<div className='flex flex-1 flex-col gap-1'>
												<div className='flex items-center justify-between gap-2'>
													<span className='text-[14px] font-medium'>{commentEntry.authorName}</span>
													<span className='text-[12px] text-606060'>
														{formatPreferenceDateTime(commentEntry.createdAt)}
													</span>
												</div>
												<p className='text-[14px] text-foreground/90'>{commentEntry.body}</p>
											</div>
										</div>
									))}
								</div>
							)}
							{actorCan.comment ? (
								<form
									className='flex flex-col gap-2'
									onSubmit={e => {
										e.preventDefault()
										if (commentBody.trim()) onComment()
									}}
								>
									<Textarea
										value={commentBody}
										onChange={e => commentBody$.set(e.target.value)}
										placeholder={c.commentPlaceholder.value}
										onKeyDown={e => {
											if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
												e.preventDefault()
												if (commentBody.trim()) onComment()
											}
										}}
									/>
									<Button type='submit' size='sm' className='self-start' disabled={!commentBody.trim()}>
										{c.commentAction}
									</Button>
								</form>
							) : null}
						</div>
					</AccordionContent>
				</AccordionItem>
			</Accordion>

			<Separator />

			<Accordion type='multiple'>
				<AccordionItem value='history' className='border-b-0'>
					<AccordionTrigger className='text-[16px] font-medium py-0'>{c.sectionHistory}</AccordionTrigger>
					<AccordionContent className='pt-3'>
						{item.history.length === 0 ? (
							<p className='text-[13px] text-606060'>{c.noHistory}</p>
						) : (
							<div className='flex flex-col'>
								{item.history.map(entry => {
									const entryDetail = historyDetail(c, memberName, entry)
									return (
										<div key={entry.id} className='flex items-center gap-2 py-1 min-w-0'>
											{historyIcon(entry.action)}
											<span className='text-[12px] text-foreground/80 font-medium shrink-0'>
												{entry.actorName}
											</span>
											<span className='text-[12px] text-606060 truncate flex-1'>
												{historyLabel(c, entry)}
												{entryDetail ? <span className='text-foreground/50'> · {entryDetail}</span> : null}
											</span>
											<span className='text-[11px] text-606060/70 shrink-0 tabular-nums'>
												{formatPreferenceDateTime(entry.createdAt)}
											</span>
										</div>
									)
								})}
							</div>
						)}
					</AccordionContent>
				</AccordionItem>
			</Accordion>
		</div>
	)
}

export { WorkItemMain }
